import axiosInstance from "../common/axiosInstance";
import { ConvertTaskAction } from "../../utils/types";
import { useQuery, UseQueryResult, useMutation, UseMutationResult, UseMutationOptions } from "@tanstack/react-query";

export const PATHS = Object.freeze({
  GET_ACTION_ITEMS: "/interactions/action-items",
  PAGE_ACTION_ITEMS: "/action-items",
  POST_CREATE_ACTION_ITEM: (interactionId?: string) =>
    interactionId
      ? `/action-items/create?interactionId=${interactionId}`
      : `/action-items/create`,
  DELETE_ACTION_ITEM: "/action-items/delete",
  PATCH_UPDATE_ACTION_ITEM: '/action-items/update',
  CLIENT_ACTION_ITEMS: "/client-actionItems",
  GET_USER_ACTION_ITEMS: "/user-action-items",
  GET_ACTION_ITEMS_DETAILS: "/action-item-details",
  SUBSCRIBED: "/action-items/subscribe",
  GET_SUBSCRIBED_ACTION_ITEMS: "/user-subscribed-action-items",
  POST_GROUP_BY_ACTION_ITEMS: "/group-by-action-items",
  GET_ACTION_ITEM_TAGS: "/action-item-tags",
  POST_ACTION_ITEM_TAGS: "/action-item/tags-upsert",
  CONVERT_ACTION_ITEM_TO_TASK:"/action-item/convert-task",
  GET_ACTION_ITEM_HISTORY: "/action-item/history",
  GET_ACTION_ITEM_SPACES: "/action-item-spaces",
  POST_ACTION_ITEM_SPACES: "/action-item-space-upsert",
  GET_GLOBAL_SEARCH_ACTION_ITEMS: "/interactions/global-search-action-items",
  ADD_ATTACHMENTS: "/action-items/add-attachments",
  DOWNLOAD_ATTACHMENTS: "/action-item/download-attachments",
  GET_ACTIONITEMS_DASHBOARDS: "/action-item/dashboard",
  DELETE_ATTACHMENTS: "/action-item/delete-attachments",
  POST_ACTION_ITEM_COUNT: "/action-items/count",
  GET_AI_RECOMMENDED_TASKS_EXPORT: "/action-items/export",
});

interface GetActionItemsParams {
  interactionId: string;
  isTeamInteraction: boolean;
  sharedWithMe?: boolean;
  sharedByMe?: boolean; 
  assetId?: string;
  impersonatingPorId?: string | undefined;
  accessLevel?: string | undefined;
}
interface UpdateActionItemParams {
  title?: string;
  dueDate?: string;
  revised_due_date?: string;
  assigneePorId?: string;
  status?: string;
  attachments?: any[];
  tagIds?: any[];
  actionSubscribersPorIds?: string[] | null;
  deleteSubscribersPorIds?: string[] | null;
  coachPorId?: string;    
  priority?: string| null;
  description?: string| null;
  actionItemId?: any;
  interactionId?: string | null;
  subTasks?: SubTaskItem[];
}

interface DeleteActionItemParams {
  actionItemId: string[];
}

interface GetActionItemsDashBoardParams {
  startTime: string;
  endTime: string;
  isForTeam: boolean;
  employeePorId: string;
  dashboardView: string;
  status: string;
  includeManagerTasks?: boolean;
}
interface CreateActionItemParams {
  description?: string;
  dueDate?: string;
  assigneePorId?: string;
  status?: string;
  attachments?: any[];
  title?: string;
  relatedTo?: string;
  coachPorId?: string;
  priority?: string;
  sponsorPorId?: string;
  isSupportTicket?: boolean;
  titles?: string[];
  parentActionItemId?: string;
}
interface GetPageActionItemsParams {
  employeePorId: string;
  isForTeam: boolean;
  assignorPorIds?: string[];
  actionItem?: string;
  clientOrgIdsForFilters?: string;
  assigneePorIds?: string[];
  status?: string;
  startTime: string;
  endTime: string;
  offset: number;
  limit: number;
  sortBy: string;
  sortOrder: string;
  isForAccount?: boolean;
  isTrackingActionItems?: boolean;
  priority?: string;
  coachPorIds?: string[];
  interactionId?: string;
  clientOrgId?: string;
  tagIds?: string[];
  sponsorPorIds?: string[];
  isNotClientTasks?: boolean;
  subscriberPorIds?: string[];
  subscriberUserGroupIds?: string[];
  __groupExpandToken?: string;
  isTaskOverview?: boolean;
  delayCategory?: string;
  includeManagerTasks?: boolean;
  isCompleteMomTask?: boolean;
  isAiRecommendedActionItems?: boolean;
  createdAtStartTime?: string;
  createdAtEndTime?: string;
  isAiRecommendedUnassignedActionItems?: boolean;
  isNonRecurringInteractionTasks?: boolean;
  isTaskFromFingertipMetrics?: boolean;
  subOrgId?: string;
  aiActionItemsStates?: string[];
}

interface GetAiRecommendedTasksExportParams {
  employeePorId: string;
  isForTeam: boolean;
  assignorPorIds?: string[];
  actionItem?: string;
  clientOrgIdsForFilters?: string[];
  assigneePorIds?: string[];
  status?: string;
  startTime?: string;
  endTime?: string;
  sortBy: string;
  sortOrder: string;
  isForAccount?: boolean;
  isTrackingActionItems?: boolean;
  priority?: string;
  coachPorIds?: string[];
  interactionId?: string;
  clientOrgId?: string;
  tagIds?: string[];
  sponsorPorIds?: string[];
  isNotClientTasks?: boolean;
  subscriberPorIds?: string[];
  subscriberUserGroupIds?: string[];
  __groupExpandToken?: string;
  isTaskOverview?: boolean;
  delayCategory?: string;
  includeManagerTasks?: boolean;
  isCompleteMomTask?: boolean;
  isAiRecommendedActionItems?: boolean;
  createdAtStartTime?: string;
  createdAtEndTime?: string;
  isAiRecommendedUnassignedActionItems?: boolean;
  isNonRecurringInteractionTasks?: boolean;
  isTaskFromFingertipMetrics?: boolean;
  subOrgId?: string;
  aiActionItemsStates?: string[];
}

interface GetClientActionItemsParams {
  employeeEmail: string;
  clientDomain: string;
  startTime: string;
  endTime: string;
  offset: number;
  limit: number;
  sortBy: string;
  sortOrder: string;
}

interface GetUserActionItemsParams {
  employeePorId: string;
  startTime: string;
  endTime: string;
  offset: number;
  limit: number;
  sortBy: string;
  sortOrder: string;
}

interface GetSubscribedParams {
  actionItemIds: string[];
  subscriberProfileOrgIdId: string;
  isSubscribed: boolean;
}

interface GetUserSubscribedActionItemsParams {
  employeeEmail: string;
  assignee?: string;
  client?: string;
  status?: string;
  actionItem?: string;
  isForTeam?: boolean;
  startTime: string;
  endTime: string;
  offset: number;
  limit: number;
}

interface GetActionItemDetailsParams {
  actionItemId: string;
  isSubTask?: boolean;
}

interface GetGroupByActionItemsParams {
  startTime: string;
  endTime: string;
  groupByActionItem: string;
  clientOrgIdsForFilters?: string;
  assigneePorIds?: string;
  status?: string;
  coachPorIds?: string;
  assignorPorIds?: string;
  priority?: string;
  actionItem?: string;
  isForAccount?: boolean;
  isForTeam?: boolean;
  isTrackingActionItems?: boolean;
  tagIds?: string[];
  sponsorPorIds?: string[];
  subscriberUserGroupIds?: string[];
  subscriberPorIds?: string[];
  isNotClientTasks?: boolean;
  isTaskOverview?: boolean;
  employeePorId?: string;
  includeManagerTasks?: boolean;
  isCompleteMomTask?: boolean;
  isAiRecommendedActionItems?: boolean;
  aiActionItemsStates?: string[];
  isGroupByView?: boolean;
  createdAtStartTime?: string;
  createdAtEndTime?: string;
}

interface GetActionItemTagsParams {
  tagId?: string;
  name: string;
  isDeleted: boolean;
}

export interface SubTaskItem {
  actionItemId: string;
  assigneePorId?: string;
  dueDate?: string;
}

export interface ConvertActionItemToTaskParams {
  interactionId?: string;
  actionItemIds: string[];
  action: ConvertTaskAction;
}

interface GetSpacesParams {
  title: string;
  spaceMemberProfileOrgIds?: string[];
  description?: string;
  subOrgIds?: string[];
}

interface UpdateSpacesParams {
  title: string;
  description?: string;
  spaceId?: string;
  spaceMemberProfileOrgIds?: string[];
  removeMemberProfileOrgIds?: string[];
  subOrgIds?: string[];
  removeSubOrgIds?: string[];
}

interface DeleteSpaceParams {
  accountId: string;
  spaceId: string;
}

interface GetGlobalSearchActionItemsParams {
  limit: number;
  offset: number;
  sortOrder: string;
  sortBy: string;
  searchProfileOrgId: string;
  employeePorId: string;
  startTime: string;
  endTime: string;
}

interface GetActionItemsCountParams {
  employeePorId: string;
  isForTeam: boolean;
  assignorPorIds?: string[];
  actionItem?: string;
  clientOrgIdsForFilters?: string;
  assigneePorIds?: string[];
  status?: string;
  startTime: string;
  endTime: string;
  isForAccount?: boolean;
  isTrackingActionItems?: boolean;
  priority?: string;
  coachPorIds?: string[];
  interactionId?: string;
  clientOrgId?: string;
  tagIds?: string[];
  sponsorPorIds?: string[];
  isNotClientTasks?: boolean;
  subscriberPorIds?: string[];
  subscriberUserGroupIds?: string[];
  __groupExpandToken?: string;
  isTaskOverview?: boolean;
  delayCategory?: string;
  includeManagerTasks?: boolean;
  isCompleteMomTask?: boolean;
  isAiRecommendedActionItems?: boolean;
  createdAtStartTime?: string;
  createdAtEndTime?: string;
  isAiRecommendedUnassignedActionItems?: boolean;
  isNonRecurringInteractionTasks?: boolean;
  isTaskFromFingertipMetrics?: boolean;
  subOrgId?: string;
  aiActionItemsStates?: string[];
}
const getUserActionItems = async (
  params: GetUserActionItemsParams
): Promise<any> => {
  const response = await axiosInstance.get(
    PATHS.GET_USER_ACTION_ITEMS,{
      params: {
        employeePorId: params.employeePorId,
        startTime: params.startTime,
        endTime: params.endTime,
        offset: params.offset,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
      }
    }
  );
  return response.data;
};

const useGetAllUserActionItems = (
  params: GetUserActionItemsParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_user_action_items", stringifiedParams],
    queryFn: () => getUserActionItems(params),
    enabled,
    retry: 0,
  });
};

const getActionItems = async (
  params: GetActionItemsParams
): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_ACTION_ITEMS, {
    params: {
      interactionId: params.interactionId,
      isTeamInteraction: params.isTeamInteraction,
      ...(params.sharedWithMe && { sharedWithMe: params.sharedWithMe }),
      ...(params.sharedByMe !== undefined ? { sharedByMe: params.sharedByMe } : {}),
      ...(params.assetId && { assetId: params.assetId }),
      ...(params.impersonatingPorId && { impersonatingPorId: params.impersonatingPorId }),
      ...(params.accessLevel && { accessLevel: params.accessLevel }),
    },
  });
  return response;
};

const useGetActionItems = (
  params: GetActionItemsParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_action_items", stringifiedParams],
    queryFn: () => getActionItems(params),
    enabled,
    retry: 0,
  });
};

const getPageActionItems = async (
  params: GetPageActionItemsParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.PAGE_ACTION_ITEMS,
    {
      employeePorId: params.employeePorId,
      isForTeam: params.isForTeam,
      startTime: params.startTime,
      endTime: params.endTime,
      ...(params.assignorPorIds && { assignorPorIds: params.assignorPorIds }),
      ...(params.actionItem && { actionItem: params.actionItem }),
      ...(params.clientOrgIdsForFilters && { clientOrgIdsForFilters: params.clientOrgIdsForFilters }),
      ...(params.assigneePorIds && { assigneePorIds: params.assigneePorIds }),
      ...(params.status && { status: params.status }),
      ...(params.isForAccount && { isForAccount: params.isForAccount }),
      ...(params.isTrackingActionItems && { isTrackingActionItems: params.isTrackingActionItems }),
      ...(params.priority && { priority: params.priority }),
      ...(params.coachPorIds && { coachPorIds: params.coachPorIds }),
      ...(params.interactionId && { interactionId: params.interactionId }),
      ...(params.clientOrgId && { clientOrgId: params.clientOrgId }),
      ...(params.tagIds && { tagIds: params.tagIds}),
      ...(params.sponsorPorIds && {sponsorPorIds: params.sponsorPorIds}),
      ...(params.isNotClientTasks && { isNotClientTasks: params.isNotClientTasks}),
      ...(params.subscriberPorIds && { subscriberPorIds: params.subscriberPorIds}),
      ...(params.subscriberUserGroupIds && { subscriberUserGroupIds: params.subscriberUserGroupIds}),
      ...(params.__groupExpandToken && { __groupExpandToken: params.__groupExpandToken}),
      ...(params.isTaskOverview && { isTaskOverview: params.isTaskOverview}),
      ...(params.delayCategory && { delayCategory: params.delayCategory}),
      ...(params.includeManagerTasks && { includeManagerTasks: params.includeManagerTasks}),
      ...(params.isCompleteMomTask && { isCompleteMomTask: params.isCompleteMomTask}),
      ...(params.isAiRecommendedActionItems && { isAiRecommendedActionItems: params.isAiRecommendedActionItems}),
      ...(params.createdAtStartTime && { createdAtStartTime: params.createdAtStartTime}),
      ...(params.createdAtEndTime && { createdAtEndTime: params.createdAtEndTime}),
      ...(params.isAiRecommendedUnassignedActionItems && { isAiRecommendedUnassignedActionItems: params.isAiRecommendedUnassignedActionItems}),
      ...(params.isNonRecurringInteractionTasks && { isNonRecurringInteractionTasks: params.isNonRecurringInteractionTasks }),
      ...(params.isTaskFromFingertipMetrics && { isTaskFromFingertipMetrics: params.isTaskFromFingertipMetrics}),
      ...(params.subOrgId && { subOrgId: params.subOrgId}),
      ...(params.aiActionItemsStates && { aiActionItemsStates: params.aiActionItemsStates }),
    },
    {
      params: {
        offset: params.offset,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
      },
    }
  );
  return response;
};

const useGetPageActionItems = (
  params: GetPageActionItemsParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_my_action_items", stringifiedParams],
    queryFn: () => getPageActionItems(params),
    enabled,
    retry: 0,
    staleTime: 0,
  });
};

const updateActionItem = async (
  actionItemIds: string[],
  updateActionItemParams: UpdateActionItemParams
) => {
  const response = await axiosInstance.put(
    PATHS.PATCH_UPDATE_ACTION_ITEM,
    updateActionItemParams,
    {
      params: {
        actionItemId: actionItemIds,
      },
    }
  );
  return response;
};

const useUpdateActionItem = (actionItemId: string[] | string | undefined, handlerFunctions: any = {}) => {
  // Normalize to array
  const safeIds = Array.isArray(actionItemId)
    ? actionItemId
    : actionItemId
    ? [actionItemId]
    : [];

  return useMutation<any, any, UpdateActionItemParams>({
    mutationKey: ['update_action_item', ...safeIds],
    mutationFn: (updateActionItemParams: UpdateActionItemParams) =>
      updateActionItem(safeIds, updateActionItemParams).then((res) => res),
    ...handlerFunctions,
  });
};


const createActionItem = async (meeting_id: string, createActionItemParams: CreateActionItemParams) => {
  const response = await axiosInstance.post(PATHS.POST_CREATE_ACTION_ITEM(meeting_id), createActionItemParams);
  return response;
}

const usePostCreateActionItem = (meeting_id: string, handlerFunctions: any) => {
  return useMutation<any, any, any>({
    mutationKey: ['create_action_item', meeting_id],
    mutationFn: (createActionItemParams: CreateActionItemParams) => createActionItem(meeting_id, createActionItemParams).then((res) => res),
    ...handlerFunctions
  })
}

const removeActionItem = async (
  params: DeleteActionItemParams
): Promise<any> => {
  const ids = Array.isArray(params.actionItemId)
    ? params.actionItemId
    : [params.actionItemId];

  const response = await axiosInstance.delete(PATHS.DELETE_ACTION_ITEM, {
    params: {
      actionItemId: ids,
    },
  });

  return response;
};

const useDeleteActionItem = (
  actionItemId: string[] | string,
  handlerFunctions = {}
): UseMutationResult<any, any, DeleteActionItemParams> => {
  const safeIds = Array.isArray(actionItemId)
    ? actionItemId
    : actionItemId
    ? [actionItemId]
    : [];

  return useMutation({
    mutationKey: ["delete_action_item", ...safeIds],
    mutationFn: (params: DeleteActionItemParams) => removeActionItem(params).then((res) => res),
    ...handlerFunctions,
  });
};


const getClientActionItems = async (
  params: GetClientActionItemsParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.CLIENT_ACTION_ITEMS,
    {
      employeeEmail: params.employeeEmail,
      clientDomain: params.clientDomain,
      startTime: params.startTime,
      endTime: params.endTime,
    },
    {
      params: {
        offset: params.offset,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
      },
    }
  );
  return response;
};

const useGetClientActionItems = (
  params: GetClientActionItemsParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_client_action_items", stringifiedParams],
    queryFn: () => getClientActionItems(params),
    enabled,
    retry: 0,
  });
};

const subscribeActionItem = async (
  params: GetSubscribedParams
): Promise<any> => {
  const response = await axiosInstance.post(PATHS.SUBSCRIBED, {
    actionItemId: params.actionItemIds,
    subscriberProfileOrgIdId: params.subscriberProfileOrgIdId,
    isSubscribed: params.isSubscribed,
  });

  return response;
};

const useSubscribeActionItem  = (handlerFunctions: any = {}) => {
  return useMutation<any, any, GetSubscribedParams>({
    mutationKey: ["subscribe_action_item"],
    mutationFn: (params: GetSubscribedParams) => {
      return subscribeActionItem(params);
    },
    ...handlerFunctions,
  });
};

const getUserSubscribedActionItems = async (
  params: GetUserSubscribedActionItemsParams,
): Promise<any> => {
  const response = await axiosInstance.post(
    `${PATHS.GET_SUBSCRIBED_ACTION_ITEMS}?offset=${params.offset}&limit=${params.limit}`,
    {
      employeeEmail: params.employeeEmail,
      assignee: params.assignee,
      client: params.client,
      status: params.status,
      actionItem: params.actionItem,
      isForTeam: params.isForTeam,
      startTime: params.startTime,
      endTime: params.endTime,
    }
  );
  return response.data;
};

const useGetUserSubscribedActionItems = (
  params: GetUserSubscribedActionItemsParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_user_subscribed_action_items", stringifiedParams],
    queryFn: () => getUserSubscribedActionItems(params),
    enabled,
    retry: 0,
  });
};
const getActionItemDetails = async (
  params: GetActionItemDetailsParams
): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_ACTION_ITEMS_DETAILS, {
    params: {
      actionItemId: params.actionItemId,
      isSubTask: Boolean(params.isSubTask),
    },
  });
  return response;
};

// --- add this hook near other useQuery hooks ---
const useGetActionItemDetails = (
  params: GetActionItemDetailsParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  return useQuery({
    queryKey: ["get_action_item_details", params.actionItemId, Boolean(params.isSubTask)],
    queryFn: () => getActionItemDetails(params),
    enabled: enabled && !!params.actionItemId,
    retry: 0,
  });
};

const getGroupByActionItems = async (
  params: GetGroupByActionItemsParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.POST_GROUP_BY_ACTION_ITEMS,
    {
      startTime: params.startTime,
      endTime: params.endTime,
      ...(params.assignorPorIds && { assignorPorIds: params.assignorPorIds }),
      ...(params.actionItem && { actionItem: params.actionItem }),
      ...(params.clientOrgIdsForFilters && { clientOrgIdsForFilters: params.clientOrgIdsForFilters }),
      ...(params.assigneePorIds && { assigneePorIds: params.assigneePorIds }),
      ...(params.status && { status: params.status }),
      ...(params.priority && { priority: params.priority }),
      ...(params.coachPorIds && { coachPorIds: params.coachPorIds }),
      ...(params.isForAccount && { isForAccount: params.isForAccount }),
      isForTeam: params.isForTeam,
      ...(params.isTrackingActionItems && { isTrackingActionItems: params.isTrackingActionItems }),
      ...(params.tagIds && { tagIds: params.tagIds}),
      ...(params.sponsorPorIds && {sponsorPorIds: params.sponsorPorIds}),
      ...(params.subscriberUserGroupIds && {subscriberUserGroupIds: params.subscriberUserGroupIds}),
      ...(params.subscriberPorIds && {subscriberPorIds: params.subscriberPorIds}),
      ...(params.isNotClientTasks && { isNotClientTasks: params.isNotClientTasks}),
      ...(params.isTaskOverview && { isTaskOverview: params.isTaskOverview}),
      ...(params.employeePorId && { employeePorId: params.employeePorId}),
      ...(params.includeManagerTasks && { includeManagerTasks: params.includeManagerTasks}),
      ...(params.isCompleteMomTask && { isCompleteMomTask: params.isCompleteMomTask}),
      ...(params.isAiRecommendedActionItems && { isAiRecommendedActionItems: params.isAiRecommendedActionItems}),
      ...(params.aiActionItemsStates && { aiActionItemsStates: params.aiActionItemsStates }),
      ...(params.createdAtStartTime && { createdAtStartTime: params.createdAtStartTime }),
      ...(params.createdAtEndTime && { createdAtEndTime: params.createdAtEndTime }),
    },
    {
      // Query parameters
      params: {
        groupByActionItem: params.groupByActionItem,
      },
    }
  );
  return response;
};

const useGetGroupByActionItems = (
  params: GetGroupByActionItemsParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_group_by_action_items", stringifiedParams],
    queryFn: () => getGroupByActionItems(params),
    enabled,
    retry: 0,
  });
};

const getActionItemTags = async(): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_ACTION_ITEM_TAGS);
  return response;
};

const useGetActionItemTags = (
  enabled: boolean
): UseQueryResult<any, any> => {
  return useQuery<any>({
    queryKey: ["get_action_item_tags"],
    queryFn: () => getActionItemTags(),
    enabled,
    retry: 0,
  });
};

const createActionItemTags = async(
  params: GetActionItemTagsParams
) => {
  const response = await axiosInstance.post(PATHS.POST_ACTION_ITEM_TAGS, params);
  return response;
};

const useCreateActionItemTags = ( handlerFunctions: any) => {
  return useMutation<any, any, any>({
    mutationKey: ["create_action_item_tags"],
    mutationFn: (params: GetActionItemTagsParams) => createActionItemTags(params).then((res) => res),
    ...handlerFunctions
  })
};

const updateActionItemTags = async(params: GetActionItemTagsParams) => {
  const response = await axiosInstance.post(PATHS.POST_ACTION_ITEM_TAGS, params);
  return response;
};

const useUpdateActionItemTags = (handlerFunctions: any) => {
  return useMutation<any, any, any>({
    mutationKey: ["update_action_item_tags"],
    mutationFn: (params: GetActionItemTagsParams) => updateActionItemTags(params).then((res) => res),
    ...handlerFunctions
  })
};

const deleteActionItemTags = async(params: GetActionItemTagsParams) => {
  const response = await axiosInstance.post(PATHS.POST_ACTION_ITEM_TAGS, params);
  return response;
};

const useDeleteActionItemTags = (handlerFunctions : any ={}) => {
  return useMutation<any, any, any>({
    mutationKey: ["delete_actionItem_tags"],
    mutationFn: (params: GetActionItemTagsParams) => deleteActionItemTags(params).then((res) => res),
    ...handlerFunctions
  })
};

const convertActionItemToTask = async (
  params: ConvertActionItemToTaskParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.CONVERT_ACTION_ITEM_TO_TASK,
    params.actionItemIds,
    {
      params: {
        interactionId: params.interactionId,
        action: params.action,
      },
    }
  );
  return response;
};


const useConvertActionItemToTask = (handlerFunctions: any = {}) => {
  return useMutation<any, any, ConvertActionItemToTaskParams>({
    mutationKey: ["convert_action_item_to_task"],
    mutationFn: (params: ConvertActionItemToTaskParams) =>
      convertActionItemToTask(params).then((res) => res),
    ...handlerFunctions,
  })
}

const getActionItemHistory = async(params: GetActionItemDetailsParams) => {
  const { actionItemId } = params;
  const response = await axiosInstance.get(PATHS.GET_ACTION_ITEM_HISTORY,{ params: { actionItemId } });
  return response;
};

const useGetActionItemHistory = (
  params: GetActionItemDetailsParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  return useQuery<any>({
    queryKey: ["get_action_item_history", params.actionItemId],
    queryFn: () => getActionItemHistory(params),
    enabled,
    retry: 0,
  });
};

const getSpaces = async(): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_ACTION_ITEM_SPACES);
  return response;
};

const useGetSpaces = (
  enabled: boolean
): UseQueryResult<any, any> => {
  return useQuery<any>({
    queryKey: ["get_spaces"],
    queryFn: () => getSpaces(),
    enabled,
    retry: 0,
  });
};

const createSpaces = async(
  params: GetSpacesParams
)=> {
  const response = await axiosInstance.post(PATHS.POST_ACTION_ITEM_SPACES, params);
  return response;
};

const useCreateSpaces = (handlerFunctions: any) => {
  return useMutation<any, any, any>({
    mutationKey: ["create_spaces"],
    mutationFn: (params: GetSpacesParams) => createSpaces(params).then((res) => res),
    ...handlerFunctions
  })
};

const updateSpaces = async(
  params: UpdateSpacesParams
) => {
  const response = await axiosInstance.post(PATHS.POST_ACTION_ITEM_SPACES, params);
  return response;
}

const useUpdateSpaces = (handlerFunctions: any) => {
  return useMutation<any, any, any>({
    mutationKey: ["update_spaces"],
    mutationFn: (params: UpdateSpacesParams) => updateSpaces(params).then((res) => res),
    ...handlerFunctions
  })
}

const deleteSpace = async (params: DeleteSpaceParams) => {
  const { accountId, spaceId } = params;
  const response = await axiosInstance.delete(
    `action-item-space/${spaceId}/delete?accountId=${accountId}`
  );
  return response;
};

const useDeleteSpace = (handlerFunctions: any) => {
  return useMutation<any, any, any>({
    mutationKey: ["delete_space"],
    mutationFn: (params: DeleteSpaceParams) =>
      deleteSpace(params).then((res) => res),
    ...handlerFunctions,
  });
};

const getGlobalSearchActionItems = async (
  params: GetGlobalSearchActionItemsParams
): Promise<any> => {
  const {
    limit,
    offset,
    sortOrder,
    searchProfileOrgId,
    employeePorId,
    startTime,
    endTime,
    sortBy,
  } = params;

  const response = await axiosInstance.post(
    PATHS.GET_GLOBAL_SEARCH_ACTION_ITEMS,
    {
      searchProfileOrgId,
      employeePorId,
      startTime,
      endTime,
    },
    {
      params: {
        limit,
        offset,
        sortOrder,
        sortBy,
      },
    }
  );

  return response.data;
};


const useGetGlobalSearchActionItems = (
  params: GetGlobalSearchActionItemsParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery<any>({
    queryKey: ["global_search_action_items", stringifiedParams],
    queryFn: () => getGlobalSearchActionItems(params),
    enabled,
    retry: 0,
  });
};

const addAttachment = async (
  params: {
    actionItemId: string;
    file: File | Blob;
  }
): Promise<any> => {
  const formData = new FormData();
  formData.append("file", params.file);

  const response = await axiosInstance.post(PATHS.ADD_ATTACHMENTS,
    formData,
    {
      params: {
        actionItemId: params.actionItemId,
      },
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response;
};

const useAddAttachment = (
  options?: UseMutationOptions<any, any, {
    file: File;
    actionItemId: string;
  }>
): UseMutationResult<any, any, {
    file: File;
    actionItemId: string;
}> => {
  return useMutation({
    mutationFn: addAttachment,
    ...options,
  });
};

const downloadAttachment = async ({
  attachmentPath,
}: {
  attachmentPath: string;
}): Promise<string> => {
  const response = await axiosInstance.get(PATHS.DOWNLOAD_ATTACHMENTS,
    {
      params: { attachmentPath },
    }
  );
  return response.data;
};

const useDownloadAttachment = (
  options?: UseMutationOptions<
    string,
    any,
    { attachmentPath: string }
  >
): UseMutationResult<
  string,
  any,
  { attachmentPath: string }
> => {
  return useMutation({
    mutationFn: downloadAttachment,
    ...options,
  });
};

const deleteAttachment = async (params: {
  actionItemId: string;
  attachmentPath: string;
}): Promise<any> => {
  const response = await axiosInstance.delete(PATHS.DELETE_ATTACHMENTS,
    {
      params: {
        actionItemId: params.actionItemId,
        attachmentPath: params.attachmentPath,
      },
    }
  );
  return response.data;
};

const useDeleteAttachment = (
  options?: UseMutationOptions< any, any,
    {
      actionItemId: string;
      attachmentPath: string;
    }
  >
): UseMutationResult< any, any,
  {
    actionItemId: string;
    attachmentPath: string;
  }
> => {
  return useMutation({
    mutationFn: deleteAttachment,
    ...options,
  });
};


const getActionItemsDashboard = async (
  params: GetActionItemsDashBoardParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.GET_ACTIONITEMS_DASHBOARDS,
    {
      startTime: params.startTime,
      endTime: params.endTime,
      isForTeam: params.isForTeam,
      employeePorId: params.employeePorId,
      dashboardView: params.dashboardView,
      status: params.status,
      includeManagerTasks: params.includeManagerTasks,
    }
  );

  return response.data;
};

const useGetActionItemsDashboard = (
  params: GetActionItemsDashBoardParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery<any>({
    queryKey: ["get_action_items_dashboard", stringifiedParams],
    queryFn: () => getActionItemsDashboard(params),
    enabled,
    retry: 0,
  });
};

const getActionItemCount = async (
  params: GetActionItemsCountParams,
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.POST_ACTION_ITEM_COUNT,
    {
      employeePorId: params.employeePorId,
      isForTeam: params.isForTeam,
      startTime: params.startTime,
      endTime: params.endTime,
      ...(params.assignorPorIds && { assignorPorIds: params.assignorPorIds }),
      ...(params.actionItem && { actionItem: params.actionItem }),
      ...(params.clientOrgIdsForFilters && { clientOrgIdsForFilters: params.clientOrgIdsForFilters }),
      ...(params.assigneePorIds && { assigneePorIds: params.assigneePorIds }),
      ...(params.status && { status: params.status }),
      ...(params.isForAccount && { isForAccount: params.isForAccount }),
      ...(params.priority && { priority: params.priority }),
      ...(params.coachPorIds && { coachPorIds: params.coachPorIds }),
      ...(params.interactionId && { interactionId: params.interactionId }),
      ...(params.clientOrgId && { clientOrgId: params.clientOrgId }),
      ...(params.tagIds && { tagIds: params.tagIds}),
      ...(params.sponsorPorIds && {sponsorPorIds: params.sponsorPorIds}),
      ...(params.isNotClientTasks && { isNotClientTasks: params.isNotClientTasks}),
      ...(params.subscriberPorIds && { subscriberPorIds: params.subscriberPorIds}),
      ...(params.subscriberUserGroupIds && { subscriberUserGroupIds: params.subscriberUserGroupIds}),
      ...(params.isTaskOverview && { isTaskOverview: params.isTaskOverview}),
      ...(params.includeManagerTasks && { includeManagerTasks: params.includeManagerTasks}),
      ...(params.isCompleteMomTask && { isCompleteMomTask: params.isCompleteMomTask}),
      ...(params.isAiRecommendedActionItems && { isAiRecommendedActionItems: params.isAiRecommendedActionItems}),
      ...(params.createdAtStartTime && { createdAtStartTime: params.createdAtStartTime}),
      ...(params.createdAtEndTime && { createdAtEndTime: params.createdAtEndTime}),
      ...(params.isAiRecommendedUnassignedActionItems && { isAiRecommendedUnassignedActionItems: params.isAiRecommendedUnassignedActionItems}),
      ...(params.isNonRecurringInteractionTasks && { isNonRecurringInteractionTasks: params.isNonRecurringInteractionTasks }),
      ...(params.isTaskFromFingertipMetrics && { isTaskFromFingertipMetrics: params.isTaskFromFingertipMetrics}),
      ...(params.isTrackingActionItems && { isTrackingActionItems: params.isTrackingActionItems }),
      ...(params.delayCategory && { delayCategory: params.delayCategory}),
      ...(params.aiActionItemsStates && { aiActionItemsStates: params.aiActionItemsStates }),
    }
  );
  return response.data;
};

const useGetActionItemCount = (
  params: GetActionItemsCountParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_action_item_count", stringifiedParams],
    queryFn: () => getActionItemCount(params),
    enabled,
    retry: 0,
  });
};

const getAiRecommendedTasksExport = async (
  params: GetAiRecommendedTasksExportParams
): Promise<Blob> => {
  const response = await axiosInstance.post(
    PATHS.GET_AI_RECOMMENDED_TASKS_EXPORT,
    {
      employeePorId: params.employeePorId,
      isForTeam: params.isForTeam,
      ...(params.startTime && { startTime: params.startTime }),
      ...(params.endTime && { endTime: params.endTime }),
      ...(params.assignorPorIds && { assignorPorIds: params.assignorPorIds }),
      ...(params.actionItem && { actionItem: params.actionItem }),
      ...(params.clientOrgIdsForFilters?.length && { clientOrgIdsForFilters: params.clientOrgIdsForFilters }),
      ...(params.assigneePorIds && { assigneePorIds: params.assigneePorIds }),
      ...(params.status && { status: params.status }),
      ...(params.isForAccount && { isForAccount: params.isForAccount }),
      ...(params.isTrackingActionItems && { isTrackingActionItems: params.isTrackingActionItems }),
      ...(params.priority && { priority: params.priority }),
      ...(params.coachPorIds && { coachPorIds: params.coachPorIds }),
      ...(params.interactionId && { interactionId: params.interactionId }),
      ...(params.clientOrgId && { clientOrgId: params.clientOrgId }),
      ...(params.tagIds && { tagIds: params.tagIds}),
      ...(params.sponsorPorIds && {sponsorPorIds: params.sponsorPorIds}),
      ...(params.isNotClientTasks && { isNotClientTasks: params.isNotClientTasks}),
      ...(params.subscriberPorIds && { subscriberPorIds: params.subscriberPorIds}),
      ...(params.subscriberUserGroupIds && { subscriberUserGroupIds: params.subscriberUserGroupIds}),
      ...(params.__groupExpandToken && { __groupExpandToken: params.__groupExpandToken}),
      ...(params.isTaskOverview && { isTaskOverview: params.isTaskOverview}),
      ...(params.delayCategory && { delayCategory: params.delayCategory}),
      ...(params.includeManagerTasks && { includeManagerTasks: params.includeManagerTasks}),
      ...(params.isCompleteMomTask && { isCompleteMomTask: params.isCompleteMomTask}),
      ...(params.isAiRecommendedActionItems && { isAiRecommendedActionItems: params.isAiRecommendedActionItems}),
      ...(params.createdAtStartTime && { createdAtStartTime: params.createdAtStartTime}),
      ...(params.createdAtEndTime && { createdAtEndTime: params.createdAtEndTime}),
      ...(params.isAiRecommendedUnassignedActionItems && { isAiRecommendedUnassignedActionItems: params.isAiRecommendedUnassignedActionItems}),
      ...(params.isNonRecurringInteractionTasks && { isNonRecurringInteractionTasks: params.isNonRecurringInteractionTasks }),
      ...(params.isTaskFromFingertipMetrics && { isTaskFromFingertipMetrics: params.isTaskFromFingertipMetrics}),
      ...(params.subOrgId && { subOrgId: params.subOrgId}),
      ...(params.aiActionItemsStates && { aiActionItemsStates: params.aiActionItemsStates }),
    },
    {
      params: {
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
      },
      responseType: "blob",
    }
  );
  return response.data;
};

const useExportAiRecommendedTasks = (
  handlerFunctions: any = {}
): UseMutationResult<Blob, any, GetAiRecommendedTasksExportParams> => {
  return useMutation({
    mutationKey: ["export_ai_recommended_tasks"],
    mutationFn: (params: GetAiRecommendedTasksExportParams) =>
      getAiRecommendedTasksExport(params),
    ...handlerFunctions,
  });
};

export interface ActionItemService {
  readonly useGetActionItems: (
    params: GetActionItemsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetPageActionItems: (
    params: GetPageActionItemsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useUpdateActionItem: (
    actionItemIds: string[],
    handlerFunctions?: any
  ) => UseMutationResult<any, any, UpdateActionItemParams>;

  readonly usePostCreateActionItem: (
    meeting_id: string,
    handlerFunctions?: any
  ) => UseMutationResult<any, any, CreateActionItemParams>;

  readonly useDeleteActionItem: (
    actionItemId: string[],
    handlerFunctions?: any
  ) => UseMutationResult<any, any, DeleteActionItemParams>;

  readonly useGetClientActionItems: (
    params: GetClientActionItemsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;
  
  readonly useGetAllUserActionItems: (
    params: GetUserActionItemsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useSubscribeActionItem: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, GetSubscribedParams>;

  readonly useGetUserSubscribedActionItems: (
    params: GetUserSubscribedActionItemsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetActionItemDetails: (
    params: GetActionItemDetailsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetGroupByActionItems: (
    params: GetGroupByActionItemsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetActionItemTags: (
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useCreateActionItemTags: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, GetActionItemTagsParams>;

  readonly useUpdateActionItemTags: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, GetActionItemTagsParams>;

  readonly useDeleteActionItemTags: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, GetActionItemTagsParams>;

    readonly useConvertActionItemToTask: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, ConvertActionItemToTaskParams>;
  readonly useGetActionItemHistory: (
    params: GetActionItemDetailsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetSpaces: (
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useCreateSpaces: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, GetSpacesParams>;

  readonly useUpdateSpaces: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, UpdateSpacesParams>;

  readonly useDeleteSpace: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, DeleteSpaceParams>;

  readonly useGetGlobalSearchActionItems: (
    params: GetGlobalSearchActionItemsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useAddAttachment: (
    options?: UseMutationOptions<any, any, {
      file: File;
      actionItemId: string;
    }>
  ) => UseMutationResult<any, any, {
    file: File;
    actionItemId: string;
  }>;

  readonly useDownloadAttachment: (
    options?: UseMutationOptions<string, any, { attachmentPath: string }>
  ) => UseMutationResult<
    string,
    any,
    { attachmentPath: string }
  >;
  
  readonly useGetActionItemsDashboard: (
    params: GetActionItemsDashBoardParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useDeleteAttachment: (
    options?: UseMutationOptions<any, any,
      {
        actionItemId: string;
        attachmentPath: string;
      }
    >
  ) => UseMutationResult<any, any,
    {
      actionItemId: string;
      attachmentPath: string;
    }
  >;

  readonly useGetActionItemCount: (
    params: GetActionItemsCountParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useExportAiRecommendedTasks: (
    handlerFunctions?: any
  ) => UseMutationResult<Blob, any, GetAiRecommendedTasksExportParams>;
  
}

const ActionItemService: ActionItemService = Object.freeze({
  useGetActionItems,
  useGetPageActionItems,
  useUpdateActionItem,
  usePostCreateActionItem,
  useDeleteActionItem,
  useGetClientActionItems,
  useGetAllUserActionItems,
  useSubscribeActionItem,
  useGetUserSubscribedActionItems,
  useGetActionItemDetails,
  useGetGroupByActionItems,
  useGetActionItemTags,
  useCreateActionItemTags,
  useUpdateActionItemTags,
  useDeleteActionItemTags,
  useConvertActionItemToTask,
  useGetActionItemHistory,
  useGetSpaces,
  useCreateSpaces,
  useUpdateSpaces,
  useDeleteSpace,
  useGetGlobalSearchActionItems,
  useAddAttachment,
  useDownloadAttachment,
  useGetActionItemsDashboard,
  useDeleteAttachment,
  useGetActionItemCount,
  useExportAiRecommendedTasks,
});

export default ActionItemService;
